<template>
  <select
    :class="['uk-select', className]"
    :value="value ?? ''"
    @change="onChange"
    :disabled="disabled"
  >
    <option v-if="showNoneOption" value="" v-text="$t('none')"></option>
    <option v-if="showAllOption" value="" v-text="$t('all')"></option>
    <option v-for="role in roles" :key="role.id" :value="role.id">
      {{ getRoleName(role) }}
    </option>
  </select>
</template>

<script lang="ts">
import { Options as Component, Prop, Vue, Watch } from 'vue-property-decorator';
import type {
  IAppCustomerRoleWithAllowDeny,
  IAppGlobalRoleWithAllowDeny,
} from '@license-manager/types';
import { namespace } from 'vuex-class';
import { Action } from '@license-manager/casl-rules';

const approles = namespace('approles');

@Component({
  name: 'RoleSelect',
  emits: ['change'],
  i18n: {
    messages: {
      de: {
        all: 'Alle',
        none: 'Keine',
      },
      en: {
        all: 'All',
        none: 'None',
      },
    },
  },
})
class RoleSelect extends Vue {
  @Prop({ required: false, default: null })
  value!: number | null;

  @Prop({ type: Boolean, default: true })
  showAllOption!: boolean;

  @Prop({ type: Boolean, default: false })
  showNoneOption!: boolean;

  @Prop({ type: String, default: '' })
  className!: string; // Renamed from 'class' to 'className'

  @Prop({ type: Boolean, default: false })
  disabled!: boolean;

  @Prop({ type: Number, required: false })
  customerId?: number;

  @Prop({ type: Boolean, default: false })
  displayOnlyGlobalRoles!: boolean;

  @approles.Getter('getAllRoles')
  getAllRoles!: (
    customerId?: number
  ) => Array<IAppGlobalRoleWithAllowDeny | IAppCustomerRoleWithAllowDeny>;

  @approles.Getter('getGlobalRoles')
  getGlobalRoles!: () => Array<IAppGlobalRoleWithAllowDeny>;

  @approles.Action('loadRoles')
  loadRoles: (params: {
    includeRights: boolean;
    loadAllRoles?: boolean;
    loadGlobalRoles?: boolean;
    customerId?: number;
  }) => Promise<void>;

  get roles() {
    if (this.displayOnlyGlobalRoles) {
      return this.getGlobalRoles();
    } else {
      return this.getAllRoles(this.customerId);
    }
  }

  getRoleName(
    role: IAppGlobalRoleWithAllowDeny | IAppCustomerRoleWithAllowDeny
  ): string {
    const name = this.$getLocalizedText(role.name, this.$i18n.locale);
    const showCustomerName =
      this.$ability.can(Action.Manage, 'Customer') && !this.customerId;
    const customerName =
      (role as IAppCustomerRoleWithAllowDeny)?.customer?.name || '';

    return (
      name + (showCustomerName && customerName ? ` (${customerName})` : '')
    );
  }

  @Watch('customerId')
  async onCustomerIdChange() {
    await this.loadRoles({
      includeRights: false,
      loadGlobalRoles: this.customerId ? false : true,
      customerId: this.customerId,
    });
  }

  async mounted() {
    if (!this.roles.length) {
      await this.loadRoles({
        includeRights: false,
        loadGlobalRoles: this.customerId ? false : true,
        customerId: this.customerId,
      });
    }
  }

  onChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.$emit('change', { target: { value: target.value === '' ? null : Number(target.value) } });
  }
}

export default RoleSelect;
</script>
